import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { TOKEN_STORAGE_KEY } from './config';
import { setSessionToken } from './useAccessToken';
import { CONSOLE_AUTH_HANDOFF_QUERY_PARAM, consumeConsoleAuthToken } from '../utils/consoleHandoff';

function hasStoredToken(): boolean {
  return typeof window !== 'undefined' && !!window.sessionStorage.getItem(TOKEN_STORAGE_KEY);
}

/**
 * Route guard. Renders <c>children</c> when a session token is in
 * sessionStorage; otherwise navigates to <c>/login</c>, preserving the
 * attempted path AND its query string on <c>location.state.from</c> so the
 * login page can bounce back after a successful sign-in (see
 * `LoginPage.tsx`'s `from` handling — without that, a query-string handoff
 * like the one below would arrive back on `/home`, having lost its flags).
 *
 * The plain "already have a token" path is a synchronous sessionStorage
 * read — we don't verify the token's signature or expiry here; the API does
 * that on every request. When the server rejects an expired token
 * mid-session, the global 401 handler in <c>apiFetch</c> clears the storage
 * and redirects, which lands on this guard's "no token" branch on the next
 * render.
 *
 * Console → analyser auth handoff (belt-and-braces, Task 4): before
 * rejecting for "no token", check whether the destination URL carries a
 * `?consoleAuthHandoff=<key>` flag (the console dialog's "Analyse capture"
 * button stashes one alongside the capture — see `utils/consoleHandoff.ts`)
 * and, if a still-valid (≤60 s) one-shot stash is sitting behind it, consume
 * it and seed THIS tab's sessionStorage token instead of bouncing to
 * /login. RequireAuth is the seam every protected route already funnels
 * through, and — critically — the ONLY place that sees the destination's
 * query string *before* the redirect decision is made; a per-page effect
 * (the pattern SasAnalyzerPage already uses to consume its capture stash)
 * runs AFTER this guard, so by the time it mounts the query string would
 * already be gone from the address the user landed on.
 *
 * The consume-and-delete itself runs in a `useEffect`, not the render body:
 * a render can be started and thrown away without committing (React can —
 * and under concurrent features, does — discard/replay renders), and
 * `consumeConsoleAuthToken` is a one-shot delete-on-read. Doing that in
 * render body risked a throwaway render permanently burning the stash
 * before the render that actually reaches the screen ever saw it — the
 * token would just be gone. An effect only ever runs after a render has
 * committed, so the token is consumed at most once per real mount.
 * `handoffKey` (computed in render, from the URL only — no storage side
 * effect) is null whenever a token is already present, so the effect below
 * is a no-op then; while resolving a genuine handoff candidate we render
 * `null` rather than redirecting prematurely, which is what makes this
 * "before the auth gate rejects" rather than "after".
 *
 * StrictMode (main.tsx wraps the app in it) double-invokes effects in dev
 * — mount, synchronous no-op "cleanup" (this effect has none), mount again.
 * The second invocation calls `consumeConsoleAuthToken` on the SAME key,
 * which the first invocation already deleted, so it returns `null` and
 * changes nothing: no double-consumption, no lost token, no flicker.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [hasToken, setHasToken] = useState(hasStoredToken);

  // Pure — reads the URL only, no storage mutation — so it's safe to
  // compute on every render. Only non-null when there's genuinely something
  // to resolve: no stored token yet, and the destination URL carries the
  // handoff flag.
  const handoffKey = !hasToken
    ? new URLSearchParams(location.search).get(CONSOLE_AUTH_HANDOFF_QUERY_PARAM)
    : null;
  const [resolvingHandoff, setResolvingHandoff] = useState(() => !!handoffKey);

  useEffect(() => {
    if (!handoffKey) return;
    const handedOffToken = consumeConsoleAuthToken(handoffKey);
    if (handedOffToken) {
      setSessionToken(handedOffToken);
      setHasToken(true);
    }
    setResolvingHandoff(false);
    // `handoffKey` is the only thing this effect depends on; re-running it
    // for an unrelated re-render would try to consume an already-spent
    // one-shot key (harmless — returns null — but pointless).
  }, [handoffKey]);

  if (!hasToken && resolvingHandoff) {
    // Still resolving a candidate handoff — render nothing rather than
    // redirect prematurely. Resolves within the same commit under React
    // Testing Library's `act()`-flushed effects; in the real app this is a
    // single paint at most.
    return null;
  }

  if (!hasToken) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}
